import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FileUpload,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import {
  NexusFrameworkIngestService,
  FrameworkIngestApiError,
} from '../services/nexus-api';
import { getUserAlias } from '../auth/midway';
import { listAccessibleTeams } from '../services/teamsService';
import {
  CSV_COLUMNS,
  REQUIRED_COLUMNS,
  downloadTemplate,
  validateRows,
} from './FrameworkIngest/csvTemplate';

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Active', value: 'active' },
  { label: 'Deprecated', value: 'deprecated' },
  { label: 'Archived', value: 'archived' },
];

const FrameworkIngest = () => {
  const serviceRef = useRef(new NexusFrameworkIngestService());
  const service = serviceRef.current;
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [publisher, setPublisher] = useState('');
  const [category, setCategory] = useState('');

  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState(null);

  const [files, setFiles] = useState([]);
  const [parsedControls, setParsedControls] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [parseWarning, setParseWarning] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);

  const enabled = service.isEnabled();

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setTeamsLoading(true);
    setTeamsError(null);
    listAccessibleTeams()
      .then((teams) => {
        if (cancelled) return;
        const options = teams.map((t) => ({
          label: t.groupId,
          value: t.groupId,
          description: t.groupType,
        }));
        setTeamOptions(options);
      })
      .catch((err) => {
        if (cancelled) return;
        setTeamsError(err.message || 'Failed to load teams');
      })
      .finally(() => {
        if (!cancelled) setTeamsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const handleFiles = ({ detail }) => {
    setFiles(detail.value);
    setParsedControls([]);
    setParseErrors([]);
    setParseWarning(null);
    setVersionConflict(false);
    setSubmitError(null);

    const file = detail.value?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const headerFields = result.meta?.fields || [];
        const missingRequiredCols = REQUIRED_COLUMNS.filter(
          (c) => !headerFields.includes(c),
        );
        if (missingRequiredCols.length > 0) {
          setParseErrors([
            {
              row: 0,
              message: `CSV is missing required column(s): ${missingRequiredCols.join(', ')}. Download the template for the expected format.`,
            },
          ]);
          return;
        }
        const unknownCols = headerFields.filter(
          (c) => !CSV_COLUMNS.includes(c),
        );
        if (unknownCols.length > 0) {
          setParseWarning(
            `Ignoring unknown column(s): ${unknownCols.join(', ')}`,
          );
        }
        const { controls, errors } = validateRows(result.data);
        setParsedControls(controls);
        setParseErrors(errors);
      },
      error: (err) => {
        setParseErrors([{ row: 0, message: `CSV parse failed: ${err.message}` }]);
      },
    });
  };

  const previewItems = useMemo(
    () => parsedControls.slice(0, 5),
    [parsedControls],
  );

  const canSubmit =
    !submitting &&
    name.trim() &&
    version.trim() &&
    selectedTeam !== null &&
    parsedControls.length > 0 &&
    parseErrors.length === 0;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setVersionConflict(false);
    try {
      const requestedBy = (await getUserAlias()) || 'unknown';
      await service.createFramework({
        framework: {
          name: name.trim(),
          version: version.trim(),
          ownerBindleResource: selectedTeam.value,
          description: description.trim() || undefined,
          status: status.value,
          publisher: publisher.trim() || undefined,
          category: category.trim() || undefined,
        },
        controls: parsedControls,
        requestedBy,
      });
      navigate('/explorer');
    } catch (err) {
      if (
        err instanceof FrameworkIngestApiError &&
        err.statusCode === 409
      ) {
        setVersionConflict(true);
      }
      setSubmitError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!enabled) {
    return (
      <Container header={<Header variant="h1">Ingest framework</Header>}>
        <Alert type="info" header="Feature disabled">
          Framework ingestion is not yet available in this environment. The
          feature is currently enabled in Beta only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h1"
          description="Create a new compliance framework by uploading a CSV of controls."
          actions={
            <Button iconName="download" onClick={downloadTemplate}>
              Download CSV template
            </Button>
          }
        >
          Ingest framework
        </Header>
      }
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() => navigate('/explorer')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Create framework
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          {submitError && !versionConflict && (
            <Alert type="error" header="Failed to create framework">
              {submitError.message}
            </Alert>
          )}

          <FormField
            label="Framework name"
            description="Unique framework identifier, e.g. NIST-800-53."
          >
            <Input
              value={name}
              onChange={({ detail }) => {
                setName(detail.value);
                setVersionConflict(false);
                setSubmitError(null);
              }}
              placeholder="e.g. NIST-800-53"
            />
          </FormField>

          <FormField
            label="Version"
            description="Bump this to ingest a new revision of an existing framework."
            errorText={
              versionConflict
                ? `Version "${version}" already exists for "${name}". Bump the version to add a new revision.`
                : undefined
            }
          >
            <Input
              value={version}
              onChange={({ detail }) => {
                setVersion(detail.value);
                setVersionConflict(false);
                setSubmitError(null);
              }}
              placeholder="e.g. 5.1.1"
            />
          </FormField>

          <FormField label="Description (optional)">
            <Textarea
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              rows={3}
            />
          </FormField>

          <FormField label="Status">
            <Select
              selectedOption={status}
              onChange={({ detail }) => setStatus(detail.selectedOption)}
              options={STATUS_OPTIONS}
            />
          </FormField>

          <FormField label="Publisher (optional)">
            <Input
              value={publisher}
              onChange={({ detail }) => setPublisher(detail.value)}
              placeholder="e.g. NIST"
            />
          </FormField>

          <FormField label="Category (optional)">
            <Input
              value={category}
              onChange={({ detail }) => setCategory(detail.value)}
              placeholder="e.g. Federal"
            />
          </FormField>

          <FormField
            label="Owning team"
            description="Only members of this team will see the framework in Control Explorer."
            errorText={teamsError || undefined}
          >
            <Select
              selectedOption={selectedTeam}
              onChange={({ detail }) => setSelectedTeam(detail.selectedOption)}
              options={teamOptions}
              loadingText="Loading teams…"
              placeholder="Select a team"
              statusType={teamsLoading ? 'loading' : teamsError ? 'error' : 'finished'}
              empty="No teams available. You must be a member of a team on the main app Bindle."
              disabled={teamsLoading || teamsError !== null}
            />
          </FormField>

          <FormField
            label="Controls CSV"
            description={`Required columns: ${REQUIRED_COLUMNS.join(', ')}. Optional: ${CSV_COLUMNS.filter((c) => !REQUIRED_COLUMNS.includes(c)).join(', ')}.`}
          >
            <FileUpload
              value={files}
              onChange={handleFiles}
              accept=".csv,text/csv"
              i18nStrings={{
                uploadButtonText: () => 'Choose CSV',
                dropzoneText: () => 'Drop CSV file to upload',
                removeFileAriaLabel: (idx) => `Remove file ${idx + 1}`,
                limitShowFewer: 'Show fewer',
                limitShowMore: 'Show more',
                errorIconAriaLabel: 'Error',
              }}
              showFileLastModified
              showFileSize
              showFileThumbnail={false}
              constraintText="CSV up to 2000 rows"
            />
          </FormField>

          {parseWarning && (
            <Alert type="warning" header="Unknown columns">
              {parseWarning}
            </Alert>
          )}

          {parseErrors.length > 0 && (
            <Alert type="error" header={`${parseErrors.length} CSV error(s)`}>
              <ul>
                {parseErrors.slice(0, 10).map((e, i) => (
                  <li key={i}>
                    {e.row > 0 ? `Row ${e.row}: ` : ''}
                    {e.message}
                  </li>
                ))}
                {parseErrors.length > 10 && (
                  <li>…and {parseErrors.length - 10} more</li>
                )}
              </ul>
            </Alert>
          )}

          {parsedControls.length > 0 && (
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">
                Preview ({parsedControls.length} control
                {parsedControls.length === 1 ? '' : 's'})
              </Box>
              <Table
                columnDefinitions={[
                  { id: 'controlId', header: 'Control ID', cell: (i) => i.controlId },
                  { id: 'title', header: 'Title', cell: (i) => i.title },
                  { id: 'category', header: 'Category', cell: (i) => i.category || '-' },
                  { id: 'priority', header: 'Priority', cell: (i) => i.priority || '-' },
                ]}
                items={previewItems}
                variant="embedded"
                empty="No rows parsed yet"
              />
              {parsedControls.length > previewItems.length && (
                <Box color="text-body-secondary">
                  Showing first {previewItems.length} of{' '}
                  {parsedControls.length} rows.
                </Box>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Form>
    </Container>
  );
};

export default FrameworkIngest;
